import React, { useCallback, useState } from 'react';

import { cssMap } from '@atlaskit/css';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { HierarchyData } from '@atlassian/reporting-lines/hierarchy-chart';
import { HierarchyModal } from '@atlassian/reporting-lines/hierarchy-chart';

import { ActionButtons } from './components/ActionButtons';
import { Avatar } from './components/Avatar';
import { UserContent } from './components/UserContent';

const NoopComponent = () => null;

const styles = cssMap({
	root: {
		position: 'relative',
		borderRadius: token('radius.large'),
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		backgroundColor: token('elevation.surface.sunken'),

		'&:hover': {
			backgroundColor: token('elevation.surface.raised.hovered'),
		},
	},
});

type UserInlinePreviewData = {
	accountId: string;
	name: string;
	avatarUrl?: string | null;
	jobTitle?: string | null;
	email?: string | null;
};

interface UserInlinePreviewProps {
	data: UserInlinePreviewData;
	hierarchyData?: HierarchyData;
	testId?: string;
	cloudId: string;
}

/**
 * Compact inline user preview with avatar, name, job title, and hover-revealed actions.
 *
 * @internal Not ready for consumption. Do not use in production.
 */
export const UserInlinePreview = componentWithFG(
	'pt_user_inline_preview_enabled',
	NoopComponent,
	UserInlinePreviewComponent,
);

function UserInlinePreviewComponent({
	data,
	hierarchyData,
	testId = 'user-inline-preview',
	cloudId,
}: UserInlinePreviewProps) {
	const { accountId, name, avatarUrl, jobTitle, email } = data;
	const [isHovered, setIsHovered] = useState(false);
	const [isHierarchyModalOpen, setIsHierarchyModalOpen] = useState(false);

	const handleOrgChartClick = useCallback(() => {
		if (hierarchyData) {
			setIsHierarchyModalOpen(true);
		}
	}, [hierarchyData]);

	const onViewOrgChartForActions = hierarchyData ? handleOrgChartClick : undefined;

	return (
		<>
			<Box
				xcss={styles.root}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				testId={testId}
			>
				<Inline space="space.150" alignBlock="center">
					<Avatar src={avatarUrl} name={name} size="medium" />
					<UserContent name={name} jobTitle={jobTitle} />
					<ActionButtons
						name={name}
						accountId={accountId}
						email={email}
						onViewOrgChart={onViewOrgChartForActions}
						isVisible={isHovered}
						cloudId={cloudId}
					/>
				</Inline>
			</Box>

			{isHierarchyModalOpen && hierarchyData && (
				<HierarchyModal
					data={hierarchyData}
					onClose={() => setIsHierarchyModalOpen(false)}
					testId="user-inline-preview-hierarchy-modal"
				/>
			)}
		</>
	);
}
